<!-- ============================================================================
     TREATMENT MANAGER COMPONENT
     Gestión de tratamientos durante la creación de citas
     Usa componentes compartidos: TreatmentItemComponent, TreatmentAddFormComponent
     ============================================================================ -->

<div
  class="vc-treatment-manager"
  [class.vc-treatment-manager--simple]="simpleLayout"
>
  <!-- LOADING STATE -->
  <div class="vc-treatment-manager__loading" *ngIf="loading">
    <p-progressSpinner
      [style]="{ width: '40px', height: '40px' }"
      strokeWidth="4"
    />
    <span>{{ "COMMON.LOADING" | translate }}...</span>
  </div>

  <!-- ERROR MESSAGE -->
  <p-message
    *ngIf="errorMessage"
    severity="error"
    [text]="errorMessage"
    styleClass="vc-treatment-manager__message"
  />

  <!-- SUCCESS MESSAGE -->
  <p-message
    *ngIf="successMessage"
    severity="success"
    [text]="successMessage"
    styleClass="vc-treatment-manager__message"
  />

  <!-- MAIN CONTENT (when not loading) -->
  <ng-container *ngIf="!loading">
    <section
      class="vc-treatment-section"
      *ngIf="showProfessionalPanel && hasAvailableTreatments"
    >
      <header class="vc-treatment-section__header">
        <div class="vc-treatment-section__title">
          <i class="fas fa-user-md"></i>
          <span>{{
            "TREATMENT_MANAGER.PROFESSIONAL.TITLE" | translate
          }}</span>
        </div>
      </header>
      <div class="vc-professional-panel">
        <p class="vc-professional-panel__description">
          {{ "TREATMENT_MANAGER.PROFESSIONAL.DESCRIPTION" | translate }}
        </p>
        <p class="vc-professional-panel__hint">
          {{
            (professionalAssignmentMode === "single"
              ? "TREATMENT_MANAGER.PROFESSIONAL.HELP_SINGLE"
              : "TREATMENT_MANAGER.PROFESSIONAL.HELP_PER_TREATMENT"
            ) | translate
          }}
        </p>

        <div class="vc-professional-panel__grid">
          <div class="field vc-professional-panel__field">
            <label class="mb-2 d-block">{{
              "TREATMENT_MANAGER.PROFESSIONAL.MODE_LABEL" | translate
            }}</label>
            <p-select
              [appendTo]="'body'"
              [options]="assignmentModeOptions"
              [(ngModel)]="professionalAssignmentMode"
              [optionLabel]="'label'"
              [optionValue]="'value'"
              (onChange)="onAssignmentModeChange($event.value)"
              fluid
            ></p-select>
          </div>

          <div
            class="field vc-professional-panel__field"
            *ngIf="professionalAssignmentMode === 'single'"
          >
            <label class="mb-2 d-block">{{
              "TREATMENT_MANAGER.PROFESSIONAL.SINGLE_LABEL" | translate
            }}</label>
            <p-select
              [appendTo]="'body'"
              [options]="getGlobalProfessionalOptions()"
              [(ngModel)]="globalProfessionalId"
              [showClear]="true"
              optionLabel="fullName"
              optionValue="id"
              [placeholder]="'COMMON.OPTIONAL' | translate"
              (onChange)="onGlobalProfessionalChanged($event.value)"
              fluid
            ></p-select>
          </div>
        </div>

        <p class="vc-professional-panel__warning" *ngIf="professionals.length === 0">
          <i class="fas fa-circle-info"></i>
          {{ "TREATMENT_MANAGER.PROFESSIONAL.NO_AVAILABLE_USERS" | translate }}
        </p>
      </div>
    </section>

    <!-- ================================================================== -->
    <!-- SECTION B: ADD NEW TREATMENT (moved above list for better UX)      -->
    <!-- ================================================================== -->
    <section class="vc-treatment-add-top" *ngIf="canManagePatientTreatments">
      <button
        type="button"
        class="vc-btn-add-treatment"
        [class.vc-btn-add-treatment--active]="showAddForm"
        (click)="toggleAddForm()"
      >
        <i
          class="fas"
          [class.fa-plus]="!showAddForm"
          [class.fa-times]="showAddForm"
        ></i>
        <span>
          {{
            showAddForm
              ? ("COMMON.CANCEL" | translate)
              : ("TREATMENT_MANAGER.ADD_NEW" | translate)
          }}
        </span>
      </button>

      <app-treatment-add-form
        *ngIf="showAddForm"
        [currency]="currency"
        [saving]="savingNewTreatment"
        [submitLabel]="'TREATMENT_MANAGER.ADD_TO_PATIENT_ONLY'"
        [helpText]="'TREATMENT_MANAGER.ADD_HELP_OPTIONAL_SELECTION'"
        (submitForm)="onAddFormSubmit($event)"
      />
    </section>

    <!-- ================================================================== -->
    <!-- SECTION A: PATIENT'S AVAILABLE TREATMENTS                          -->
    <!-- ================================================================== -->
    <section class="vc-treatment-section" *ngIf="hasAvailableTreatments">
      <header class="vc-treatment-section__header">
        <div class="vc-treatment-section__title">
          <i class="fas fa-clipboard-list"></i>
          <span>{{
            "TREATMENT_MANAGER.AVAILABLE_TREATMENTS" | translate
          }}</span>
          <span class="vc-treatment-section__count"
            >({{ treatmentItems.length }})</span
          >
        </div>
        <button
          type="button"
          class="vc-btn-text vc-btn-text--sm"
          (click)="toggleSelectAll()"
          *ngIf="treatmentItems.length > 1"
        >
          {{
            areAllSelected()
              ? ("COMMON.DESELECT_ALL" | translate)
              : ("COMMON.SELECT_ALL" | translate)
          }}
        </button>
      </header>

      <!-- Treatment List using shared component -->
      <div class="vc-treatment-list">
        <app-treatment-item
          *ngFor="let item of treatmentItems; trackBy: trackByTreatmentId"
          [treatment]="item.patientTreatment"
          [mode]="'selectable'"
          [selected]="item.selected"
          [isPaid]="item.isPaid"
          [deleting]="item.deleting"
          [removingPayment]="item.removingPayment"
          [showDelete]="canManagePatientTreatments"
          [showPaymentActions]="allowPaymentActions && canManagePayments"
          [showProfessionalSelector]="showProfessionalPanel && professionalAssignmentMode === 'per_treatment'"
          [professionals]="getProfessionalOptionsForItem(item)"
          [selectedProfessionalId]="getTreatmentProfessionalId(item)"
          (toggleSelect)="toggleTreatment(item)"
          (markAsPaid)="openPaymentDialog(item)"
          (markAsUnpaid)="removePayment(item)"
          (delete)="confirmDeleteTreatment(item)"
          (professionalChange)="onTreatmentProfessionalChange(item, $event)"
        />
      </div>

      <!-- Selection Summary -->
      <div class="vc-treatment-summary" *ngIf="selectedCount > 0">
        <span class="vc-treatment-summary__count">
          {{ selectedCount }} {{ "TREATMENT_MANAGER.SELECTED" | translate }}
        </span>
        <span class="vc-treatment-summary__total">
          {{ "COMMON.TOTAL" | translate }}: {{ formatPrice(selectedTotal) }}
        </span>
      </div>
    </section>

    <!-- ================================================================== -->
    <!-- EMPTY STATE: No treatments available                               -->
    <!-- ================================================================== -->
    <section
      class="vc-treatment-empty"
      *ngIf="!hasAvailableTreatments && !showAddForm"
    >
      <div class="vc-treatment-empty__icon">
        <i class="fas fa-notes-medical"></i>
      </div>
      <p class="vc-treatment-empty__text">
        {{ "TREATMENT_MANAGER.NO_TREATMENTS" | translate }}
      </p>
      <p class="vc-treatment-empty__hint">
        {{ "TREATMENT_MANAGER.ADD_HINT" | translate }}
      </p>
    </section>
  </ng-container>
</div>
